import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { adminResetPassword } from '../lib/adminApi';
import { ShieldCheck, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';

export default function AdminResetPassword() {
  const { toast } = useToast();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const token = sp.get('token') || '';
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!token) { setError('Missing reset token. Please request a new link.'); return; }
    if (pw1.length < 10) { setError('Password must be at least 10 characters.'); return; }
    if (pw1 !== pw2) { setError('Passwords do not match.'); return; }
    setBusy(true);
    try {
      await adminResetPassword(token, pw1);
      setDone(true);
      setTimeout(() => nav('/admin/login'), 1500);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Reset failed. Please try again or request a new link.');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-white grid place-items-center p-6" data-testid="admin-reset-page">
      <div className="w-full max-w-md">
        <div className="rounded-2xl bg-white text-neutral-900 p-7 shadow-2xl">
          {done ? (
            <div className="text-center" data-testid="admin-reset-success">
              <div className="w-14 h-14 rounded-full bg-emerald-50 grid place-items-center text-emerald-600 mx-auto"><CheckCircle2 size={28} /></div>
              <h1 className="font-display text-2xl mt-4">Password updated</h1>
              <p className="text-sm text-neutral-600 mt-2">Redirecting to sign in…</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 text-teal-800"><ShieldCheck size={16} /><span className="text-xs uppercase tracking-widest">Choose a new password</span></div>
              <h1 className="font-display text-2xl mt-2">Set your new admin password</h1>
              <p className="text-sm text-neutral-500 mt-1">Minimum 10 characters. Pick something you don't reuse elsewhere.</p>
              {error && (
                <div className="mt-4 flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3" data-testid="admin-reset-error">
                  <AlertCircle size={16} className="mt-0.5 shrink-0" /> <span>{error}</span>
                </div>
              )}
              <form onSubmit={submit} className="mt-4 space-y-4">
                <div><Label>New password</Label><Input type="password" className="h-11 mt-1.5" value={pw1} onChange={(e) => setPw1(e.target.value)} data-testid="admin-reset-new" required /></div>
                <div><Label>Confirm new password</Label><Input type="password" className="h-11 mt-1.5" value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="admin-reset-confirm" required /></div>
                <Button type="submit" disabled={busy || !token} className="w-full h-11 bg-teal-800 hover:bg-teal-900 text-white" data-testid="admin-reset-submit">
                  {busy ? (<><Loader2 size={16} className="mr-1.5 animate-spin" /> Updating…</>) : 'Update password'}
                </Button>
                <div className="text-center">
                  <Link to="/admin/login" className="text-xs text-neutral-500 hover:text-neutral-900 underline underline-offset-2">Back to sign in</Link>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
